import currency from "currency.js";
import { sortBy, uniqBy } from "lodash";
import {
    TripBuilderDepartingFlightOffersFragmentFragment,
    TripBuilderGatewayFragment,
    TripBuilderReturningFlightOffersFragmentFragment,
} from "../../../../generated/graphql";
import { useTripBuilderQuote } from "../context/TripBuilderQuoteStepContext";
import {
    useClosestAvailableGateway,
    type FlightSummary,
} from "./use-closest-available-gateway";
import { useTripsWithOffers } from "./use-trips-with-offers";
export const LAND_ONLY_GATEWAY_CODE = "LO1";

export type RoundtripFlightSummary = {
    departingFlight: TripBuilderDepartingFlightOffersFragmentFragment;
    returningFlight: TripBuilderReturningFlightOffersFragmentFragment;
    price: number;
    homeGateway: TripBuilderGatewayFragment;
};

type FlightsHook = {
    /**
     * The closest available roundtrip flights to user's location based on state and city
     */
    closestRoundtripFlightSummary?: FlightSummary | null;
    /**
     * whether or not LO1 roundtrip flight packages is available
     */
    isLandOnlyAvailable: boolean;
    /**
     * All available roundtrip flight packages for the departure and extension if applicable
     */
    availableFlightPackages?: RoundtripFlightSummary[];
    /**
     * The selected roundtrip flight packages based on draft quote
     */
    selectedFlightPackages?: RoundtripFlightSummary;
    /**
     * The available airport gateway (non-landonly) options for the user to select from
     */
    airportGatewayOptions: { label: string; value: string }[];
};

const isRoundtripFlightSummary = (
    result: Partial<RoundtripFlightSummary> | null,
): result is RoundtripFlightSummary =>
    !!result?.departingFlight && !!result.returningFlight;

export const useAvailableFlightPackages = (): FlightsHook => {
    const { draftQuote } = useTripBuilderQuote();
    const tripsWithOffers = useTripsWithOffers();
    const departingFlightPrices = tripsWithOffers[0].departingFlightOffers;
    const returningFlightPrices = tripsWithOffers[0].returningFlightOffers;

    const availableFlightPackages = uniqBy(
        departingFlightPrices
            .map((departingFlight) => {
                const homeGateway = departingFlight.flight.departureGateway;
                if (!homeGateway) {
                    return null;
                }
                const returningFlight = returningFlightPrices.find(
                    (returningFlight) =>
                        returningFlight.flight.arrivalGateway?.code ===
                        homeGateway.code,
                );

                if (!returningFlight) {
                    return null;
                }

                const departingFlightPrice = currency(
                    departingFlight.discountAmount?.value ?? 0,
                ).add(departingFlight.salePrice?.value ?? 0).value;
                const returningFlightPrice = currency(
                    returningFlight.discountAmount?.value ?? 0,
                ).add(returningFlight.salePrice?.value ?? 0).value;

                return {
                    departingFlight,
                    returningFlight,
                    homeGateway,
                    price: currency(departingFlightPrice).add(
                        returningFlightPrice,
                    ).value,
                };
            })
            .filter(isRoundtripFlightSummary),
        (item) => item.homeGateway.code,
    );

    const airportFlights = availableFlightPackages.reduce(
        (acc, flightPackage) => {
            const { homeGateway, price } = flightPackage;
            if (homeGateway.code !== LAND_ONLY_GATEWAY_CODE) {
                acc.push({
                    city: homeGateway.internalCityName,
                    state: homeGateway.stateCode,
                    code: homeGateway.code,
                    price,
                    displayName: `${[
                        homeGateway.stateCode,
                        homeGateway.internalCityName,
                    ]
                        .filter(Boolean)
                        .join(", ")} (${homeGateway.code})`,
                });
            }
            return acc;
        },
        new Array<{
            city: string;
            state?: string | null;
            code: string;
            price: number;
            displayName: string;
        }>(),
    );

    const { closestRoundtripFlightSummary } =
        useClosestAvailableGateway(airportFlights);

    const airportGatewayOptions = sortBy(airportFlights, ["state"]).map(
        (flight) => ({
            label: flight.displayName,
            value: flight.code,
        }),
    );

    const isLandOnlyAvailable = availableFlightPackages.some(
        (flightPackage) =>
            flightPackage.homeGateway.code === LAND_ONLY_GATEWAY_CODE,
    );

    const selectedFlightPackages = availableFlightPackages.find(
        ({ homeGateway }) =>
            draftQuote?.order.trips[0].openDepartingFlightLineItem
                ?.departureGatewayCode === homeGateway.code,
    );

    return {
        availableFlightPackages,
        airportGatewayOptions,
        isLandOnlyAvailable,
        closestRoundtripFlightSummary,
        selectedFlightPackages,
    };
};
